import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { auth } from '../../../../queries/me';
import { sidebarQueries } from '../../../../queries/sidebar';
import type { Section } from '../../../../store/storeSidebarTree';
import { Namespace } from '../../../../types/namespace';
import { Flows, Folder } from '../../../../util/Types/Folder';
import { Space } from '../../../../util/Types/Sidebar';
import { FluxObject } from '../AgentsSidebar/context';
import type { BackendNodeKind } from './moveTarget';
import { compareRanks } from './rank';

interface MoveLocation {
  parentId: string | null;
  spaceId: string | null;
  section: Section;
}

interface OptimisticMoveArgs {
  nodeId: string;
  nodeKind: BackendNodeKind;
  from: MoveLocation;
  to: MoveLocation;
  rank: string;
}

type Ranked = { id: string; rank?: string | null };

const FOLDERS_KEY = sidebarQueries.folders().queryKey;
const SPACES_KEY = sidebarQueries.spaces().queryKey;
const ME_KEY = auth.me().queryKey;

export const sortByRank = <T extends Ranked>(list: T[]): T[] =>
  [...list].sort((a, b) => compareRanks(a.rank ?? '', b.rank ?? ''));

export const detachFolderInTree = (
  folders: Folder[],
  folderId: string,
): { detached: Folder | null; next: Folder[] } => {
  let detached: Folder | null = null;
  const next: Folder[] = [];
  for (const f of folders) {
    if (f.id === folderId) {
      detached = f;
      continue;
    }
    const sub = detachFolderInTree(f.folders ?? [], folderId);
    if (sub.detached && !detached) detached = sub.detached;
    next.push(sub.detached ? { ...f, folders: sub.next } : f);
  }
  return { detached, next };
};

export const attachFolderInTree = (
  folders: Folder[],
  parentId: string | null,
  node: Folder,
): Folder[] => {
  if (parentId === null) {
    return sortByRank([...folders.filter(f => f.id !== node.id), node]);
  }
  return folders.map(f => {
    if (f.id === parentId) {
      return {
        ...f,
        folders: sortByRank([
          ...(f.folders ?? []).filter(sub => sub.id !== node.id),
          node,
        ]),
      };
    }
    return {
      ...f,
      folders: attachFolderInTree(f.folders ?? [], parentId, node),
    };
  });
};

export const updateFlowInTree = (
  folders: Folder[],
  flowId: string,
  fromParentId: string | null,
  toParentId: string | null,
  patch: Partial<Flows>,
): { next: Folder[]; moved: Flows | null } => {
  let moved: Flows | null = null;
  const detached = folders.map(folder => {
    if (fromParentId && folder.id === fromParentId) {
      const flow = (folder.flows ?? []).find(f => f.id === flowId) ?? null;
      if (flow) {
        moved = { ...flow, ...patch };
        return {
          ...folder,
          flows: (folder.flows ?? []).filter(f => f.id !== flowId),
        };
      }
    }
    if ((folder.folders ?? []).length > 0) {
      const sub = updateFlowInTree(
        folder.folders ?? [],
        flowId,
        fromParentId,
        toParentId,
        patch,
      );
      if (sub.moved && !moved) moved = sub.moved;
      return { ...folder, folders: sub.next };
    }
    return folder;
  });
  if (!moved) return { next: detached, moved: null };
  const attached = detached.map(folder => {
    if (toParentId && folder.id === toParentId) {
      const merged: Flows = moved as Flows;
      return {
        ...folder,
        flows: sortByRank([
          ...(folder.flows ?? []).filter(f => f.id !== flowId),
          merged,
        ]),
      };
    }
    if ((folder.folders ?? []).length > 0) {
      return {
        ...folder,
        folders: attachFlowInTreeAt(folder.folders ?? [], toParentId, moved!),
      };
    }
    return folder;
  });
  return { next: attached, moved };
};

export const attachFlowInTreeAt = (
  folders: Folder[],
  parentId: string | null,
  flow: Flows,
): Folder[] => {
  return folders.map(folder => {
    if (parentId && folder.id === parentId) {
      return {
        ...folder,
        flows: sortByRank([
          ...(folder.flows ?? []).filter(f => f.id !== flow.id),
          flow,
        ]),
      };
    }
    if ((folder.folders ?? []).length > 0) {
      return {
        ...folder,
        folders: attachFlowInTreeAt(folder.folders ?? [], parentId, flow),
      };
    }
    return folder;
  });
};

const patchFolderMove = (
  qc: QueryClient,
  nodeId: string,
  from: MoveLocation,
  to: MoveLocation,
  rank: string,
): void => {
  if (from.spaceId === null && to.spaceId === null) {
    qc.setQueryData(FOLDERS_KEY, (old: any) => {
      if (!old?.foldersMap) return old;
      const existing = old.foldersMap[nodeId];
      if (!existing) return old;
      const foldersMap = {
        ...old.foldersMap,
        [nodeId]: {
          ...existing,
          parentId: to.parentId,
          rank,
          section: to.section,
        },
      };
      const folderHierarchy: Record<string, string[]> = {
        ...old.folderHierarchy,
      };
      Object.keys(folderHierarchy).forEach(parent => {
        if (folderHierarchy[parent].includes(nodeId)) {
          folderHierarchy[parent] = folderHierarchy[parent].filter(
            id => id !== nodeId,
          );
        }
      });
      if (to.parentId) {
        const siblings = (folderHierarchy[to.parentId] ?? []).filter(
          id => id !== nodeId,
        );
        const inserted = [...siblings, nodeId].sort((a, b) =>
          compareRanks(foldersMap[a]?.rank ?? '', foldersMap[b]?.rank ?? ''),
        );
        folderHierarchy[to.parentId] = inserted;
      }
      return { ...old, foldersMap, folderHierarchy };
    });
    return;
  }

  qc.setQueryData(SPACES_KEY, (old: any) => {
    if (!old) return old;
    let snapshot: Folder | null = null;
    const updated: Record<string, Space> = {};
    Object.keys(old).forEach(spaceId => {
      const space: Space = old[spaceId];
      const result = detachFolderInTree(space.folder ?? [], nodeId);
      if (result.detached && !snapshot) snapshot = result.detached;
      updated[spaceId] = { ...space, folder: result.next };
    });
    if (!snapshot && from.spaceId === null) {
      const folders = qc.getQueryData<any>(FOLDERS_KEY);
      const existing = folders?.foldersMap?.[nodeId];
      if (existing) snapshot = { ...existing, folders: [] };
    }
    if (!snapshot) return old;
    const moved: Folder = {
      ...snapshot,
      parentId: to.parentId,
      rank,
      section: to.section,
      space: to.spaceId ?? snapshot.space,
    };
    const destSpaceId = to.spaceId;
    if (destSpaceId && updated[destSpaceId]) {
      updated[destSpaceId] = {
        ...updated[destSpaceId],
        folder: attachFolderInTree(
          updated[destSpaceId].folder ?? [],
          to.parentId,
          moved,
        ),
      };
    }
    return updated;
  });

  if (from.spaceId !== null && to.spaceId === null) {
    qc.setQueryData(FOLDERS_KEY, (old: any) => {
      if (!old?.foldersMap) return old;
      const folderHierarchy: Record<string, string[]> = {
        ...old.folderHierarchy,
      };
      if (to.parentId) {
        const list = (folderHierarchy[to.parentId] ?? []).filter(
          id => id !== nodeId,
        );
        folderHierarchy[to.parentId] = [...list, nodeId];
      }
      return { ...old, folderHierarchy };
    });
  }
};

const patchFlowMove = (
  qc: QueryClient,
  flowId: string,
  from: MoveLocation,
  to: MoveLocation,
  rank: string,
): void => {
  if (from.spaceId === null && to.spaceId === null) {
    qc.setQueryData(FOLDERS_KEY, (old: any) => {
      if (!old?.flowsMap) return old;
      const existing = old.flowsMap[flowId];
      if (!existing) return old;
      const flowsMap = {
        ...old.flowsMap,
        [flowId]: { ...existing, rank, folderId: to.parentId },
      };
      const flowToFolder = { ...old.flowToFolder };
      if (to.parentId) flowToFolder[flowId] = to.parentId;
      else delete flowToFolder[flowId];
      const foldersMap = { ...old.foldersMap };
      const oldParentId = from.parentId;
      if (oldParentId && foldersMap[oldParentId]) {
        foldersMap[oldParentId] = {
          ...foldersMap[oldParentId],
          flows: (foldersMap[oldParentId].flows ?? []).filter(
            (f: Flows) => f.id !== flowId,
          ),
        };
      }
      if (to.parentId && foldersMap[to.parentId]) {
        const filtered = (foldersMap[to.parentId].flows ?? []).filter(
          (f: Flows) => f.id !== flowId,
        );
        const merged = flowsMap[flowId] as Flows;
        foldersMap[to.parentId] = {
          ...foldersMap[to.parentId],
          flows: sortByRank([...filtered, merged]),
        };
      }
      return { ...old, flowsMap, flowToFolder, foldersMap };
    });
    return;
  }

  qc.setQueryData(SPACES_KEY, (old: any) => {
    if (!old) return old;
    const sourceSpaceId = from.spaceId;
    const destSpaceId = to.spaceId;
    const updated: Record<string, Space> = { ...old };

    let movedFlow: Flows | null = null;

    if (sourceSpaceId && updated[sourceSpaceId]) {
      const space = updated[sourceSpaceId];
      const found = (space.flows ?? []).find(f => f.id === flowId);
      if (found) movedFlow = { ...found, rank, folderId: to.parentId };
      const result = updateFlowInTree(
        space.folder ?? [],
        flowId,
        from.parentId,
        sourceSpaceId === destSpaceId ? to.parentId : null,
        { rank, folderId: to.parentId },
      );
      if (result.moved && !movedFlow) movedFlow = result.moved;
      const flowsList =
        sourceSpaceId === destSpaceId && to.parentId === null && movedFlow
          ? sortByRank([
              ...(space.flows ?? []).filter(f => f.id !== flowId),
              movedFlow,
            ])
          : (space.flows ?? []).filter(f => f.id !== flowId);
      updated[sourceSpaceId] = {
        ...space,
        folder: result.next,
        flows: flowsList,
      };
    }

    if (
      destSpaceId &&
      destSpaceId !== sourceSpaceId &&
      updated[destSpaceId] &&
      movedFlow
    ) {
      const space = updated[destSpaceId];
      if (to.parentId) {
        updated[destSpaceId] = {
          ...space,
          folder: attachFlowInTreeAt(
            space.folder ?? [],
            to.parentId,
            movedFlow,
          ),
        };
      } else {
        updated[destSpaceId] = {
          ...space,
          flows: sortByRank([
            ...(space.flows ?? []).filter(f => f.id !== flowId),
            movedFlow,
          ]),
        };
      }
    }
    return updated;
  });
};

interface ItemPatch {
  folderId: string | null;
  space?: string | null;
  rank: string;
}

const patchItemInList = <
  T extends Ranked & { folderId?: string | null; space?: any },
>(
  list: T[] | undefined,
  itemId: string,
  patch: ItemPatch,
): T[] | undefined => {
  if (!list) return list;
  return list.map(item =>
    item.id === itemId
      ? ({
          ...item,
          folderId: patch.folderId,
          rank: patch.rank,
          ...(patch.space !== undefined ? { space: patch.space } : {}),
        } as T)
      : item,
  );
};

const removeFromList = <T extends { id: string }>(
  list: T[] | undefined,
  itemId: string,
): T[] => (list ?? []).filter(i => i.id !== itemId);

type ItemField = 'objects' | 'namespaces';

const getSpaceItems = <T>(space: Space, field: ItemField): T[] | undefined =>
  (space as unknown as Record<ItemField, T[] | undefined>)[field];

const findItemAnywhere = <T extends { id: string }>(
  privateList: T[] | undefined,
  spacesMap: Record<string, Space> | undefined,
  itemKey: ItemField,
  itemId: string,
): T | null => {
  const inPrivate = (privateList ?? []).find(i => i.id === itemId);
  if (inPrivate) return inPrivate;
  if (!spacesMap) return null;
  for (const space of Object.values(spacesMap)) {
    const list = getSpaceItems<T>(space, itemKey);
    const found = (list ?? []).find(i => i.id === itemId);
    if (found) return found;
  }
  return null;
};

const patchItemMove = (
  qc: QueryClient,
  itemKind: 'object' | 'vector',
  itemId: string,
  from: MoveLocation,
  to: MoveLocation,
  rank: string,
): void => {
  const field = itemKind === 'object' ? 'objects' : 'namespaces';

  const meData = qc.getQueryData<any>(ME_KEY);
  const spacesData = qc.getQueryData<Record<string, Space>>(SPACES_KEY);
  const source =
    itemKind === 'object'
      ? findItemAnywhere<FluxObject>(
          meData?.objects,
          spacesData,
          'objects',
          itemId,
        )
      : findItemAnywhere<Namespace>(
          meData?.namespaces,
          spacesData,
          'namespaces',
          itemId,
        );

  const patch: ItemPatch = {
    folderId: to.parentId,
    rank,
    space: to.spaceId,
  };

  if (from.spaceId === to.spaceId) {
    if (from.spaceId === null) {
      qc.setQueryData(ME_KEY, (old: any) => {
        if (!old) return old;
        return { ...old, [field]: patchItemInList(old[field], itemId, patch) };
      });
    } else {
      qc.setQueryData(SPACES_KEY, (old: any) => {
        if (!old || !from.spaceId || !old[from.spaceId]) return old;
        const space = old[from.spaceId];
        return {
          ...old,
          [from.spaceId]: {
            ...space,
            [field]: patchItemInList(space[field], itemId, patch),
          },
        };
      });
    }
    return;
  }

  if (!source) return;
  const moved = {
    ...source,
    folderId: to.parentId,
    rank,
    space: to.spaceId,
  };

  if (from.spaceId === null) {
    qc.setQueryData(ME_KEY, (old: any) => {
      if (!old) return old;
      return { ...old, [field]: removeFromList(old[field], itemId) };
    });
  } else {
    qc.setQueryData(SPACES_KEY, (old: any) => {
      if (!old || !from.spaceId || !old[from.spaceId]) return old;
      const space = old[from.spaceId];
      return {
        ...old,
        [from.spaceId]: {
          ...space,
          [field]: removeFromList(space[field], itemId),
        },
      };
    });
  }

  if (to.spaceId === null) {
    qc.setQueryData(ME_KEY, (old: any) => {
      if (!old) return old;
      const list = removeFromList(old[field], itemId);
      return { ...old, [field]: [...list, moved] };
    });
  } else {
    qc.setQueryData(SPACES_KEY, (old: any) => {
      if (!old || !to.spaceId || !old[to.spaceId]) return old;
      const space = old[to.spaceId];
      const list = removeFromList(
        getSpaceItems<{ id: string }>(space, field),
        itemId,
      );
      return {
        ...old,
        [to.spaceId]: { ...space, [field]: [...list, moved] },
      };
    });
  }
};

export const applyOptimisticMove = (
  qc: QueryClient,
  args: OptimisticMoveArgs,
): void => {
  const { nodeId, nodeKind, from, to, rank } = args;
  if (nodeKind === 'folder') {
    patchFolderMove(qc, nodeId, from, to, rank);
    return;
  }
  if (nodeKind === 'flow' || nodeKind === 'form') {
    patchFlowMove(qc, nodeId, from, to, rank);
    return;
  }
  if (nodeKind === 'object') {
    patchItemMove(qc, 'object', nodeId, from, to, rank);
    return;
  }
  if (nodeKind === 'vector') {
    patchItemMove(qc, 'vector', nodeId, from, to, rank);
  }
};

export const useOptimisticMove = (): ((args: OptimisticMoveArgs) => void) => {
  const qc = useQueryClient();
  return useCallback(
    (args: OptimisticMoveArgs) => applyOptimisticMove(qc, args),
    [qc],
  );
};
